import { io, Socket } from 'socket.io-client';
import { Message, Chat } from '../types/chat';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'wss://asfs-web.com';

export interface SocketEvents {
  connect: () => void;
  disconnect: () => void;
  authenticated: (data: { success: boolean }) => void;
  authentication_error: (data: { message: string }) => void;
  joined_chat: (data: { chatId: number }) => void;
  left_chat: (data: { chatId: number }) => void;
  new_message: (data: Message) => void;
  search_started: () => void;
  search_stopped: () => void;
  search_timeout: () => void;
  search_error: (data: { message: string }) => void;
  match_found: (data: Chat) => void;
  messages_read: (data: { chatId: number; userId: number }) => void;
  user_typing: (data: { userId: number; userNumber: string; chatId: number }) => void;
  user_stopped_typing: (data: { userId: number; userNumber: string; chatId: number }) => void;
  chat_created: (data: Chat) => void;
  joined_system_chat: () => void;
  left_system_chat: () => void;
  new_system_message: (data: Message) => void;
  user_joined_system: (data: { userNumber: string }) => void;
  user_left_system: (data: { userNumber: string }) => void;
  user_typing_system: (data: { userId: number; userNumber: string }) => void;
  user_stopped_typing_system: (data: { userId: number; userNumber: string }) => void;
  error: (data: { message: string }) => void;
  chat_deleted: (data: { chatId: number; deletedBy: number; deletedByUserNumber?: string }) => void;
  chat_cleared: (data: { chatId: number; clearedBy: number; clearedByUserNumber: string; timestamp: string }) => void;
  joined_group: (data: { groupId: number }) => void;
  new_group_message: (data: Message) => void;
  left_group: (data: { groupId: number }) => void;
}

interface PendingEvent {
  eventName: string;
  data: any;
  timestamp: number;
}

class SocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private isAuthenticated = false;
  private currentUser: { id: number; userNumber: string } | null = null;
  private pendingEvents: PendingEvent[] = [];
  private authenticationPromise: Promise<boolean> | null = null;
  private maxPendingEvents = 50;
  private eventTimeout = 30000;

  connect(): Socket {
    if (this.socket && this.socket.connected) {
      console.log('Повторное использование активного сокета');
      return this.socket;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.isAuthenticated = false;
    }

    console.log('Создание нового сокета');
    this.socket = io(SOCKET_URL, {
      transports: ['websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      forceNew: false,
    });

    this.setupSocketListeners();
    return this.socket;
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.isConnected = true;
      console.log('✅ Socket connected');

      if (this.currentUser) {
        this.authenticate(this.currentUser.id, this.currentUser.userNumber);
      }
    });

    this.socket.on('authenticated', (data) => {
      console.log('✅ Аутентификация успешна:', data);
      this.isAuthenticated = true;

      this.processPendingEvents();
    });

    this.socket.on('authentication_error', (data) => {
      console.error('❌ Ошибка аутентификации:', data);
      this.isAuthenticated = false;

      setTimeout(() => {
        if (this.currentUser && this.isConnected) {
          console.log('🔄 Повторная попытка аутентификации');
          this.authenticate(this.currentUser.id, this.currentUser.userNumber);
        }
      }, 2000);
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      this.isAuthenticated = false;
      console.log('Socket disconnected:', reason);

      if (reason === 'io server disconnect') {
        this.socket?.connect();
      }
    });

    this.socket.on('error', (err) => {
      console.error('Socket error:', err);

      if (err.message && err.message.includes('unauthorized')) {
        this.handleUnauthorizedError();
      }
    });

    this.socket.on('chat_created', (data) => {
      console.log('🔥 Получено событие chat_created:', data);
    });
  }

  private handleUnauthorizedError(): void {
    console.log('🔄 Обработка ошибки unauthorized');
    this.isAuthenticated = false;

    if (this.currentUser) {
      this.reconnectAndReauth();
    }
  }

  private async reconnectAndReauth(): Promise<void> {
    console.log('🔄 Переподключение и повторная авторизация');

    try {
      this.socket?.disconnect();
      await new Promise((resolve) => setTimeout(resolve, 1000));

      this.connect();

      await this.waitForConnection();

      if (this.currentUser) {
        await this.authenticate(this.currentUser.id, this.currentUser.userNumber);
      }
    } catch (error) {
      console.error('Ошибка при переподключении:', error);
    }
  }

  private waitForConnection(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isConnected) {
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Timeout waiting for connection'));
      }, 10000);

      this.socket?.once('connect', () => {
        clearTimeout(timeout);
        resolve();
      });
    });
  }

  disconnect(clearUser: boolean = false): void {
    if (this.socket) {
      this.socket.off();
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.isAuthenticated = false;
      this.pendingEvents = [];
      this.authenticationPromise = null;

      if (clearUser) {
        this.currentUser = null;
      }

      console.log('Socket полностью отключён');
    }
  }

  authenticate(userId: number, userNumber: string): Promise<boolean> {
    this.currentUser = { id: userId, userNumber };

    if (!this.socket || !this.isConnected) {
      console.warn('Сокет не подключён, ждём connect для аутентификации');
      return new Promise((resolve) => {
        this.socket?.once('connect', () => {
          resolve(this.authenticate(userId, userNumber));
        });
      });
    }

    if (this.authenticationPromise) {
      return this.authenticationPromise;
    }

    this.authenticationPromise = new Promise((resolve, reject) => {
      console.log(`🔐 Отправка аутентификации: userId=${userId}, userNumber=${userNumber}`);

      const timeout = setTimeout(() => {
        this.authenticationPromise = null;
        reject(new Error('Authentication timeout'));
      }, 10000);

      const onAuthenticated = (data: { success: boolean }) => {
        clearTimeout(timeout);
        this.socket?.off('authenticated', onAuthenticated);
        this.socket?.off('authentication_error', onError);
        this.authenticationPromise = null;

        if (data.success) {
          this.isAuthenticated = true;
          resolve(true);
        } else {
          resolve(false);
        }
      };

      const onError = (data: { message: string }) => {
        clearTimeout(timeout);
        this.socket?.off('authenticated', onAuthenticated);
        this.socket?.off('authentication_error', onError);
        this.authenticationPromise = null;
        reject(new Error(data.message));
      };

      this.socket?.once('authenticated', onAuthenticated);
      this.socket?.once('authentication_error', onError);
      this.socket?.emit('authenticate', { userId, userNumber });
    });

    return this.authenticationPromise;
  }

  private addToPendingEvents(eventName: string, data: any): void {
    const now = Date.now();
    this.pendingEvents = this.pendingEvents.filter((event) => now - event.timestamp < this.eventTimeout);

    this.pendingEvents.push({
      eventName,
      data,
      timestamp: now,
    });

    if (this.pendingEvents.length > this.maxPendingEvents) {
      this.pendingEvents = this.pendingEvents.slice(-this.maxPendingEvents);
    }

    console.log(`📦 Событие ${eventName} добавлено в очередь (${this.pendingEvents.length} в очереди)`);
  }

  private processPendingEvents(): void {
    if (this.pendingEvents.length === 0) return;

    console.log(`🚀 Обрабатываем ${this.pendingEvents.length} отложенных событий`);

    const eventsToProcess = [...this.pendingEvents];
    this.pendingEvents = [];

    eventsToProcess.forEach((event) => {
      console.log(`📤 Повторная отправка события: ${event.eventName}`);
      this.socket?.emit(event.eventName, event.data);
    });
  }

  private async ensureAuthenticated(): Promise<boolean> {
    if (this.isAuthenticated) {
      return true;
    }

    if (!this.currentUser) {
      throw new Error('Пользователь не установлен');
    }

    try {
      return await this.authenticate(this.currentUser.id, this.currentUser.userNumber);
    } catch (error) {
      console.error('Ошибка аутентификации:', error);
      return false;
    }
  }

  private async emitWithAuth(eventName: string, data: any): Promise<void> {
    if (!this.socket) {
      throw new Error('Сокет не инициализирован');
    }

    if (!this.isConnected) {
      console.warn(`Сокет не подключён для события ${eventName}, добавляем в очередь`);
      this.addToPendingEvents(eventName, data);
      return;
    }

    if (!this.isAuthenticated) {
      console.warn(`Сокет не авторизован для события ${eventName}, добавляем в очередь`);
      this.addToPendingEvents(eventName, data);

      try {
        await this.ensureAuthenticated();
      } catch (error) {
        console.error('Не удалось авторизоваться для события:', eventName);
      }
      return;
    }

    this.socket.emit(eventName, data);
  }

  async joinChat(chatId: number): Promise<void> {
    await this.emitWithAuth('join_chat', { chatId });
  }

  async leaveChat(chatId: number): Promise<void> {
    await this.emitWithAuth('leave_chat', { chatId });
  }

  async sendMessage(chatId: number, content: string, tempId?: string | null): Promise<void> {
    await this.emitWithAuth('send_message', { chatId, content, tempId });
  }

  async markAsRead(chatId: number): Promise<void> {
    await this.emitWithAuth('mark_as_read', { chatId });
  }

  async startTyping(chatId: number): Promise<void> {
    await this.emitWithAuth('typing_start', { chatId });
  }

  async stopTyping(chatId: number): Promise<void> {
    await this.emitWithAuth('typing_stop', { chatId });
  }

  async startSearching(): Promise<void> {
    await this.emitWithAuth('start_searching', {});
  }

  async stopSearching(): Promise<void> {
    await this.emitWithAuth('stop_searching', {});
  }

  async createChatByNumber(targetUserNumber: string): Promise<void> {
    console.log(`Отправка create_chat_by_number для ${targetUserNumber}`);
    await this.emitWithAuth('create_chat_by_number', { targetUserNumber });
  }

  async joinSystemChat(): Promise<void> {
    await this.emitWithAuth('join_system_chat', {});
  }

  async leaveSystemChat(): Promise<void> {
    await this.emitWithAuth('leave_system_chat', {});
  }

  async sendSystemMessage(content: string, tempId?: string): Promise<void> {
    await this.emitWithAuth('send_system_message', { content, tempId });
  }

  async startSystemTyping(): Promise<void> {
    await this.emitWithAuth('system_typing_start', {});
  }

  async stopSystemTyping(): Promise<void> {
    await this.emitWithAuth('system_typing_stop', {});
  }

  async deleteChat(chatId: number): Promise<void> {
    console.log(`Отправка delete_chat для чата ${chatId}`);
    await this.emitWithAuth('delete_chat', { chatId });
  }

  async clearChat(chatId: number): Promise<void> {
    console.log(`Отправка clear_chat для чата ${chatId}`);
    await this.emitWithAuth('clear_chat', { chatId });
  }

  async joinGroup(inviteCode: string): Promise<void> {
    await this.emitWithAuth('join_group', { inviteCode });
  }

  async sendGroupMessage(groupId: number, content: string, tempId?: string | null): Promise<void> {
    await this.emitWithAuth('send_group_message', {
      groupId,
      content,
      tempId,
    });
  }

  async leaveGroup(groupId: number): Promise<void> {
    await this.emitWithAuth('leave_group', { groupId });
  }

  on<K extends keyof SocketEvents>(event: K, callback: SocketEvents[K]): () => void {
    if (this.socket) {
      this.socket.on(event as string, callback as any);
      return () => this.socket?.off(event as string, callback as any);
    }
    return () => {};
  }

  off<K extends keyof SocketEvents>(event: K, callback?: SocketEvents[K]): void {
    if (this.socket) {
      this.socket.off(event as string, callback as any);
    }
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  getAuthenticationStatus(): boolean {
    return this.isAuthenticated;
  }

  getCurrentUser(): { id: number; userNumber: string } | null {
    return this.currentUser;
  }

  getPendingEventsCount(): number {
    return this.pendingEvents.length;
  }
}

export const socketService = new SocketService();
export default socketService;
